import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  ArrowRight,
  Download,
  PhoneCall,
  Signal,
  Sim,
  Watch,
} from "@/assets/icons/ds";
import { ButtonLink } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { MetricTiles } from "@/components/Layout/MetricTiles/MetricTiles";
import {
  SubscriptionInfoBox,
  SubscriptionInfoGroup,
  SubscriptionInfoRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail } from "@/services/model";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import {
  getFlatNameFromLocalFlatSpeed,
  getProductDescriptionSource,
  getTariffType,
  hasDifferentSuccessor,
  hasDisplayableProductDescription,
  hasWatchSim,
  isInactive,
  stripFlatName,
} from "@/utils/deviceUtils";
import {
  getCallsVolume,
  getIncludedRoamingVolumeInGb,
  getNextPaymentDate,
  getSubscriptionSpeeds,
  isFlatMobileInternationalCallsIncluded,
} from "@/utils/tariffUtils";

/**
 * The subscription card: the product the SIM runs on plus what it includes. Flat Mobile
 * subscriptions list their Swiss and international options, Flat Home and
 * data-only subscriptions their speeds.
 */
export const SubscriptionOptions = ({
  simDetail,
  action,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
}) => {
  const { t: tPortal } = useTranslation("portal");
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription",
  });
  // A SIM whose package has lapsed still knows what it renews into, and the
  // whole card describes that product — so every lookup here goes through the
  // same fallback, not just the ones that happen to have it built in.
  const serviceId = simDetail.serviceID ?? simDetail.successorForRenew?.id;
  const subscriptionType = getTariffType(serviceId);
  const speeds = getSubscriptionSpeeds(simDetail);
  const roamingVolume = getIncludedRoamingVolumeInGb(serviceId);
  // Both conditions have to hold for the calls row, so they are decided once —
  // otherwise the "International" group can end up as a header with no rows.
  const includedCallsVolume =
    (isFlatMobileInternationalCallsIncluded(serviceId) &&
      getCallsVolume({
        t: tPortal,
        tariffType: subscriptionType,
        minContractDuration: simDetail.minContractDuration,
      })) ||
    undefined;
  // An inactive SIM without any package has no product to name, but one whose
  // renewal is already planned (e.g. after a failed payment) does.
  const hasProduct =
    !isInactive(simDetail) || hasDisplayableProductDescription(simDetail);
  // Only worth announcing while the SIM still runs on its own package: without
  // `localFlatSpeed` the name above already comes from `successorForRenew`, and
  // the same product would be shown twice. Same-id Watch/Voice drops still
  // count — see `hasDifferentSuccessor`.
  const successor =
    simDetail.localFlatSpeed &&
    simDetail.autoRenew &&
    hasDifferentSuccessor(simDetail)
      ? getFlatNameFromLocalFlatSpeed(simDetail.successorForRenew?.description)
      : undefined;
  const successorDate =
    simDetail.remainingDays !== undefined
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;

  return (
    <SubscriptionCard
      dataTestid="subscription-options"
      className="self-start"
      title={t("title")}
      action={action}
    >
      <SubscriptionInfoBox className="flex items-center gap-3">
        <Sim className="size-6 shrink-0 text-foreground-primary" />
        <p className="text-body-lg font-semibold leading-md text-foreground-primary">
          {hasProduct
            ? // The Watch SIM and the Voice option get their own rows below, so
              // the product name stays bare here.
              stripFlatName(getProductDescriptionSource(simDetail) ?? "")
            : t("noSubscription")}
        </p>
      </SubscriptionInfoBox>

      {speeds && (
        <MetricTiles
          metrics={[
            {
              icon: <Download className="size-6" />,
              value: `${speeds.download}`,
              unit: t("options.maxSpeedUnit"),
              label: t("options.download"),
            },
            {
              icon: <Download className="size-6 rotate-180" />,
              value: `${speeds.upload}`,
              unit: t("options.maxSpeedUnit"),
              label: t("options.upload"),
            },
            {
              icon: <Signal className="size-6" />,
              value: speeds.network,
              unit: t("options.unlimited"),
            },
          ]}
        />
      )}

      {!speeds && hasProduct && (
        <>
          <SubscriptionInfoGroup label={t("options.switzerland")}>
            <SubscriptionInfoRow
              icon={<Signal className="size-6" />}
              title={t("options.dataAndTelephony")}
              detail={t("options.unlimited")}
            />
          </SubscriptionInfoGroup>

          {(roamingVolume || includedCallsVolume) && (
            <SubscriptionInfoGroup label={t("options.international")}>
              {roamingVolume && (
                <SubscriptionInfoRow
                  icon={<Signal className="size-6" />}
                  title={t("options.dataRoaming")}
                  detail={t("options.roamingVolume", { volume: roamingVolume })}
                />
              )}
              {includedCallsVolume && (
                <SubscriptionInfoRow
                  icon={<PhoneCall className="size-6" />}
                  title={t("options.calls")}
                  detail={includedCallsVolume}
                />
              )}
            </SubscriptionInfoGroup>
          )}
        </>
      )}

      {hasWatchSim(simDetail) && (
        <SubscriptionInfoBox className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Watch className="size-6 shrink-0 text-foreground-primary" />
            <p className="truncate text-body-lg font-semibold leading-md text-foreground-primary">
              {t("options.watch")}
            </p>
          </div>
          <ButtonLink
            data-testid="subscription-watch-sim"
            variant="secondary"
            size="sm"
            ariaLabel={t("options.watch")}
            icon={<ArrowRight />}
            to={`${portalRoutes.manageDevice.watchSim.base}?simId=${simDetail.simId}`}
            replace
          />
        </SubscriptionInfoBox>
      )}

      {successor && (
        <Notification
          dataTestid="subscription-successor"
          title={
            // The switch happens at the renewal, so the frames name that date
            // — the same one the status card bills on. Without it, only the
            // product can be announced.
            successorDate ? (
              <Trans
                t={t}
                i18nKey="successorOn"
                components={[<span key="0" className="font-semibold" />]}
                values={{ date: successorDate, flatName: successor }}
              />
            ) : (
              t("successor", { flatName: successor })
            )
          }
        />
      )}
    </SubscriptionCard>
  );
};
